import type { AllergySeverity, Customer } from '../types/customer';
import { IMG } from './images';

export const customer: Customer = {
  id: 'cus-dana',
  name: 'Dao Wattana',
  photo: IMG.kitchenIsland,
  email: 'dao.wattana@example.com',
  phone: '+66 81 555 0148',
  addressLine: 'Siri Residence, Soi Sukhumvit 24, Unit 12B',
  city: 'Phrom Phong, Bangkok',
  postcode: '10110',
  lat: 13.7305,
  lng: 100.5695,
  joinedOn: '2024-05-02',
  favouriteCuisineIds: ['thai', 'japanese', 'vietnamese'],
  savedChefIds: ['chef-kenji', 'chef-priya'],
  dietary: {
    members: [
      { id: 'hm-1', name: 'Dao', relation: 'Me' },
      { id: 'hm-2', name: 'James', relation: 'Partner' },
      { id: 'hm-3', name: 'Ing', relation: 'Daughter, 9' },
    ],
    allergies: [
      { id: 'al-1', allergen: 'Shellfish', severity: 'anaphylaxis', memberId: 'hm-1', note: 'Carries an EpiPen. No shared wok, oil or boards — shrimp paste and fish sauce with shrimp included.' },
      { id: 'al-2', allergen: 'Peanuts', severity: 'strict', memberId: 'hm-3', note: 'No peanuts in som tam, satay sauce or garnishes. Separate mortar please.' },
      { id: 'al-3', allergen: 'Dairy', severity: 'avoid', memberId: 'hm-2' },
    ],
    intolerances: ['Lactose'],
    diets: ['No beef'],
    dislikes: ['Coriander', 'Stink beans', 'Very oily fish'],
    spiceTolerance: 4,
    notes: 'Ing eats early and wants everything mild — happy for the adults to have a hotter version of the same dish. James does not eat beef.',
  },
  kitchen: {
    hob: 'induction',
    burners: 2,
    oven: 'Small combi oven, no second oven',
    fridgeSpace: 'workable',
    freezerSpace: 'tight',
    worktop: 'workable',
    dishwasher: false,
    equipment: [
      { id: 'oven', label: 'Working oven', available: true },
      { id: 'induction_pan', label: 'Induction-safe pans', available: true },
      { id: 'wok', label: 'Carbon steel wok', available: false },
      { id: 'wok_burner', label: 'High-output gas burner', available: false },
      { id: 'mortar', label: 'Granite mortar & pestle', available: false },
      { id: 'steamer', label: 'Bamboo or metal steamer', available: true },
      { id: 'sticky_steamer', label: 'Sticky rice basket & pot', available: false },
      { id: 'heavy_skillet', label: 'Heavy cast-iron skillet', available: true },
      { id: 'charcoal_grill', label: 'Charcoal grill or tao', available: false },
      { id: 'blender', label: 'High-speed blender', available: true },
      { id: 'food_processor', label: 'Food processor', available: false },
      { id: 'blowtorch', label: 'Kitchen blowtorch', available: false },
      { id: 'thermometer', label: 'Probe thermometer', available: true },
      { id: 'large_stockpot', label: 'Large stockpot', available: true },
      { id: 'sharp_knives', label: 'Sharp knives', available: true },
      { id: 'rice_cooker', label: 'Rice cooker', available: true },
    ],
    serviceWareFor: 10,
    parking: 'Two visitor bays in the basement, register at the desk with a plate number.',
    access: '12th floor, service lift on the B1 side. Deliveries must use the service lift.',
    pets: 'One cat, stays in the back bedroom during service.',
    photos: [
      { id: 'kp-1', url: IMG.kitchenBangkok, caption: 'Main run and induction hob' },
      { id: 'kp-2', url: IMG.kitchenIsland, caption: 'Island and serving space' },
      { id: 'kp-3', url: IMG.kitchenGalley, caption: 'Prep corner and shelving' },
    ],
    notes: 'Condo rules: no open flame or charcoal on the balcony. Extractor is weak, so heavy wok work smokes the unit out — chefs usually bring a portable burner.',
  },
  paymentMethods: [
    { id: 'pm-1', brand: 'Visa', last4: '4242', expiry: '11/28', holder: 'Dao Wattana', isDefault: true },
    { id: 'pm-2', brand: 'PromptPay', last4: '0148', expiry: '—', holder: 'Dao Wattana', isDefault: false },
  ],
};

export const severityLabel: Record<string, string> = {
  avoid: 'Avoid',
  strict: 'Strict — no cross-contact',
  anaphylaxis: 'Anaphylaxis risk',
};

/**
 * Severity reads as a single escalating scale everywhere it appears:
 * saffron for avoid, terracotta for strict, red for anaphylaxis.
 */
export const severityTone: Record<AllergySeverity, 'accent' | 'action' | 'danger'> = {
  avoid: 'accent',
  strict: 'action',
  anaphylaxis: 'danger',
};

/** Selected-chip classes, matched to the tone above. */
export const severityChip: Record<AllergySeverity, string> = {
  avoid: 'border-accent bg-accent-soft text-on-accent',
  strict: 'border-action bg-action-soft text-action',
  anaphylaxis: 'border-danger bg-danger-soft text-danger',
};

/** Unselected chip, tinted just enough to preview which level it sets. */
export const severityChipIdle: Record<AllergySeverity, string> = {
  avoid: 'border-hairline text-ink-3 hover:border-accent hover:text-on-accent',
  strict: 'border-hairline text-ink-3 hover:border-action hover:text-action',
  anaphylaxis: 'border-hairline text-ink-3 hover:border-danger hover:text-danger',
};

/** One-line summary that follows the customer into requests, chats, and quotes. */
export function allergySummary(): string {
  const severe = customer.dietary.allergies
    .filter((a) => a.severity !== 'avoid')
    .map((a) => `${a.allergen.toLowerCase()} (${a.severity === 'anaphylaxis' ? 'anaphylaxis' : 'strict'})`);
  const mild = customer.dietary.allergies.filter((a) => a.severity === 'avoid').map((a) => a.allergen.toLowerCase());
  const parts = [...severe, ...mild];
  const diets = [...customer.dietary.diets, ...customer.dietary.intolerances.map((i) => `${i.toLowerCase()} intolerance`)];
  return [parts.join(', '), diets.join(', ')].filter(Boolean).join(' · ');
}
